import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Loader2, UtensilsCrossed, Coffee, IceCream, GlassWater, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { value: 'entree', label: 'Entrées', icon: '🥗' },
  { value: 'plat', label: 'Plats', icon: '🍽️' },
  { value: 'dessert', label: 'Desserts', icon: '🍰' },
  { value: 'boisson', label: 'Boissons', icon: '🥤' },
  { value: 'menu', label: 'Menus/Formules', icon: '📋' }
];

const TAG_OPTIONS = [
  { value: 'vegetarien', label: 'Végétarien', color: 'bg-green-100 text-green-700' },
  { value: 'vegan', label: 'Vegan', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'sans_gluten', label: 'Sans gluten', color: 'bg-amber-100 text-amber-700' },
  { value: 'bio', label: 'Bio', color: 'bg-lime-100 text-lime-700' },
  { value: 'halal', label: 'Halal', color: 'bg-blue-100 text-blue-700' },
  { value: 'maison', label: 'Fait maison', color: 'bg-orange-100 text-orange-700' }
];

export default function RestaurantMenuPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', category: 'plat',
    is_available: true, is_takeaway: true, is_dine_in: true,
    allergens: [], tags: []
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchMenu = async () => {
    try {
      const res = await fetch(`${API}/business/menu`, { headers });
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchMenu(); }, []);

  const resetForm = () => {
    setForm({ name: '', description: '', price: '', category: 'plat', is_available: true, is_takeaway: true, is_dine_in: true, allergens: [], tags: [] });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name, description: item.description || '', price: item.price.toString(),
      category: item.category, is_available: item.is_available, is_takeaway: item.is_takeaway,
      is_dine_in: item.is_dine_in, allergens: item.allergens || [], tags: item.tags || []
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!form.name || !form.price) return toast.error("Nom et prix requis");
    setSaving(true);
    try {
      const body = { ...form, price: parseFloat(form.price) };
      const url = editingItem ? `${API}/business/menu/${editingItem.id}` : `${API}/business/menu`;
      const method = editingItem ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(editingItem ? "Plat modifié" : "Plat ajouté");
      setShowForm(false);
      resetForm();
      fetchMenu();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    try {
      await fetch(`${API}/business/menu/${id}`, { method: 'DELETE', headers });
      toast.success("Plat supprimé");
      fetchMenu();
    } catch (err) { toast.error("Erreur"); }
  };

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: items.filter(i => i.category === cat.value)
  })).filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/business/dashboard">
              <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold" data-testid="menu-page-title">Ma Carte</h1>
              <p className="text-sm text-muted-foreground">{items.length} plat(s)</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-menu-item-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UtensilsCrossed className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground mb-4">Aucun plat dans votre carte</p>
              <Button onClick={() => { resetForm(); setShowForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Ajouter un plat
              </Button>
            </CardContent>
          </Card>
        ) : (
          grouped.map(group => (
            <div key={group.value}>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span>{group.icon}</span> {group.label}
              </h2>
              <div className="space-y-2">
                {group.items.map(item => (
                  <Card key={item.id} className={`${!item.is_available ? 'opacity-60' : ''}`} data-testid={`menu-item-${item.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.name}</p>
                          {!item.is_available && <Badge variant="secondary" className="text-xs">Indisponible</Badge>}
                        </div>
                        {item.description && <p className="text-sm text-muted-foreground mt-1">{item.description}</p>}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.is_takeaway && <Badge variant="outline" className="text-xs">A emporter</Badge>}
                          {item.is_dine_in && <Badge variant="outline" className="text-xs">Sur place</Badge>}
                          {(item.tags || []).map(t => {
                            const tagDef = TAG_OPTIONS.find(to => to.value === t);
                            return <Badge key={t} className={`text-xs ${tagDef?.color || 'bg-gray-100'}`}>{tagDef?.label || t}</Badge>;
                          })}
                        </div>
                      </div>
                      <p className="text-lg font-bold">{item.price.toFixed(2)}€</p>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteItem(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="menu-item-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier le plat' : 'Ajouter un plat'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Salade César" data-testid="menu-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Ingrédients, accompagnements..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prix (€) *</Label>
                <Input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} placeholder="12.50" data-testid="menu-price-input" />
              </div>
              <div>
                <Label>Catégorie</Label>
                <select className="w-full h-9 rounded-md border bg-transparent px-3 text-sm" value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_dine_in} onCheckedChange={(v) => setForm(p => ({ ...p, is_dine_in: v }))} />
                <Label className="text-sm">Sur place</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_takeaway} onCheckedChange={(v) => setForm(p => ({ ...p, is_takeaway: v }))} />
                <Label className="text-sm">A emporter</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_available} onCheckedChange={(v) => setForm(p => ({ ...p, is_available: v }))} />
                <Label className="text-sm">Disponible</Label>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Tags</Label>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map(tag => (
                  <Badge
                    key={tag.value}
                    className={`cursor-pointer text-xs ${form.tags.includes(tag.value) ? tag.color + ' ring-2 ring-offset-1' : 'bg-gray-100 text-gray-500'}`}
                    onClick={() => toggleTag(tag.value)}
                  >
                    {tag.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button onClick={saveItem} disabled={saving} data-testid="save-menu-item-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingItem ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
